import type { Editor } from '@tiptap/react'

/** A single entry in the slash (`/`) quick-insert menu. */
export interface SlashCommandItem {
  id: string
  label: string
  /** Optional secondary text shown muted next to the label. */
  hint?: string
  /** Extra text matched against the query alongside the label. */
  keywords?: string
  /** TipTap content inserted in place of the typed `/query`. */
  content: object | string
  /** Shown grayed out and not selectable (by mouse or keyboard). */
  disabled?: boolean
}

/** A labeled run of items inside a group's flyout (a subgroup, or the group's
 *  own direct items when `label` is omitted). */
export interface SlashCommandSection {
  label?: string
  items: SlashCommandItem[]
}

/** A top-level group: one row in the menu whose flyout lists its sections. */
export interface SlashCommandGroup {
  id: string
  label: string
  sections: SlashCommandSection[]
}

/** Every item in a group, flattened across its sections. */
export function groupItems(group: SlashCommandGroup): SlashCommandItem[] {
  return group.sections.flatMap((section) => section.items)
}

/** Every item across all groups, in render order — the flat keyboard list. */
export function allGroupItems(groups: SlashCommandGroup[]): SlashCommandItem[] {
  return groups.flatMap(groupItems)
}

function itemMatches(item: SlashCommandItem, query: string): boolean {
  return (
    item.label.toLowerCase().includes(query) ||
    (item.keywords?.toLowerCase().includes(query) ?? false)
  )
}

/** Headings match on *prefix*, not substring: `/user` opens "User
 *  information", while `/info` — a fragment half the headings share — stays a
 *  plain field search instead of dumping the whole menu. */
function headingMatches(label: string | undefined, query: string): boolean {
  return label?.toLowerCase().startsWith(query) ?? false
}

/** Narrow the groups to items matching `query`, dropping empty sections and
 *  groups. A hit on the group or section heading keeps that whole run — so
 *  `/user` lists every field under "User information", not just the ones whose
 *  own label says "user". Returns the groups unchanged when the query is
 *  blank. */
export function filterGroups(
  groups: SlashCommandGroup[],
  query: string,
): SlashCommandGroup[] {
  const q = query.trim().toLowerCase()
  if (!q) return groups
  const out: SlashCommandGroup[] = []
  for (const group of groups) {
    const groupHit = headingMatches(group.label, q)
    const sections = group.sections
      .map((section) =>
        groupHit || headingMatches(section.label, q)
          ? section
          : {
              ...section,
              items: section.items.filter((item) => itemMatches(item, q)),
            },
      )
      .filter((section) => section.items.length > 0)
    if (sections.length > 0) out.push({ ...group, sections })
  }
  return out
}

/** Where the menu anchors and which text it would replace. */
export interface SlashState {
  query: string
  range: { from: number; to: number }
  rect: { bottom: number; left: number } | null
}

// `/` must open a word (start of block or after whitespace); the query runs to
// the caret and stops at the first space or slash — typing a space dismisses.
const SLASH_RE = /(?:^|\s)\/([^\s/]*)$/
const LOOKBACK = 120

/**
 * Detect an active slash command from the current selection: an empty caret in
 * a textblock with a `/query` immediately before it. Returns the query, the
 * doc range covering `/query`, and the caret rect for positioning — or null.
 */
export function computeSlashState(editor: Editor): SlashState | null {
  const { selection } = editor.state
  if (!selection.empty) return null
  const { $from } = selection
  if (!$from.parent.isTextblock) return null

  const start = Math.max(0, $from.parentOffset - LOOKBACK)
  // A hard break (Shift+Enter) is a leaf node; map it to a newline so a
  // `/` typed right after a line break still opens the menu — any other
  // leaf (e.g. a template chip) stays a non-space placeholder.
  const textBefore = $from.parent.textBetween(
    start,
    $from.parentOffset,
    '\n',
    (leaf) => (leaf.type.name === 'hardBreak' ? '\n' : '￼'),
  )
  const match = SLASH_RE.exec(textBefore)
  if (!match) return null

  const query = match[1]
  const from = selection.from - (query.length + 1)
  let rect: SlashState['rect'] = null
  try {
    const coords = editor.view.coordsAtPos(from)
    rect = { bottom: coords.bottom, left: coords.left }
  } catch {
    rect = null
  }
  return { query, range: { from, to: selection.from }, rect }
}
